import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

// Custom hook to get query parameters
export const useQueryParams = () => {
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const isStandalone = searchParams.get('standalone') === 'true';
  
  return {
    token,
    isStandalone,
    standalone: isStandalone,
    // Get all parameters as an object
    getAllParams: () => {
      const params: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
      return params;
    }
  };
};

// Custom hook to navigate with query parameters
export const useNavigateWithParams = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const navigateWithParams = (path: string, additionalParams: Record<string, string> = {}, state?: any) => {
    // Get current params
    const params = new URLSearchParams(searchParams.toString());
    
    // Add token and standalone params if they exist
    const token = params.get('token');
    if (token) {
      params.set('token', token);
    }
    
    if (params.get('standalone') === 'true') {
      params.set('standalone', 'true');
    }
    
    // Add any additional parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.set(key, value);
    });
    
    // Construct the path with query parameters
    const queryString = params.toString();
    const newPath = queryString ? `${path}?${queryString}` : path;
    
    // Navigate with state if provided
    if (state) {
      navigate(newPath, { state });
    } else {
      navigate(newPath);
    }
  };
  
  return navigateWithParams;
};

// Utility function to get current query params as object
export const getCurrentQueryParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};
  
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  
  return params;
};

// Utility function to build URL with query parameters
export const buildUrlWithParams = (path: string, additionalParams: Record<string, string> = {}) => {
  const searchParams = new URLSearchParams(window.location.search);
  
  // Add token and standalone params if they exist
  const token = searchParams.get('token');
  if (token) {
    searchParams.set('token', token);
  }
  
  if (searchParams.get('standalone') === 'true') {
    searchParams.set('standalone', 'true');
  }
  
  // Add any additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    searchParams.set(key, value);
  });
  
  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};